import {
  COMPARE_RUNS_QUERY_PARAM_NAMES,
  PAIR_DELIMITER,
} from "./ComparativeAnalysisForm.constants";
import type {
  ComparativeFormAtomTuples,
  SearchParamsType,
} from "./ComparativeAnalysisForm.types";

/** Encode a query value: safe for URL, but keep `:` readable (PIT timestamps). */
export const encodeCompareRunsQueryValue = (raw: string): string =>
  encodeURIComponent(raw).replace(/%3A/gi, ":");

const decodeRawParam = (value: string | null): string | undefined => {
  if (value == null || value === "") return undefined;
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

/** Split a paired URL param into [base, compare]. Single segment duplicates to both sides. */
export const splitPairParam = (raw: string): [string, string] => {
  const idx = raw.indexOf(PAIR_DELIMITER);
  if (idx === -1) {
    return [raw, raw];
  }
  return [raw.slice(0, idx), raw.slice(idx + PAIR_DELIMITER.length)];
};

/** Both base and compare slots must be non-empty (handles collapsed single value via split). */
export const isPairedParamComplete = (raw: string | null): boolean => {
  if (raw == null || raw === "") return false;
  const [a, b] = splitPairParam(raw);
  return a.length > 0 && b.length > 0;
};

/**
 * `task` URL param: optional when absent. When present:
 * - Collapsed value (no `~~`): same task both sides — both slots non-empty after split.
 * - Explicit pair (`a~~`, `~~b`, `a~~b`): empty side means "all tasks" — still a valid committed URL.
 */
export const isTaskParamComplete = (raw: string | null): boolean =>
  raw == null ||
  raw === "" ||
  raw.includes(PAIR_DELIMITER) ||
  isPairedParamComplete(raw);

const sanitizeForDelimiter = (value: string | undefined): string => {
  if (value == null || value === "") return "";
  if (value.includes(PAIR_DELIMITER)) {
    console.error(
      "compare-runs URL: value contained pair delimiter ~~; stripping for serialization",
      { preview: value.slice(0, 100) }
    );
    return value.replaceAll(PAIR_DELIMITER, "");
  }
  return value;
};

/**
 * Join two internal values into one URL param (collapse identical non-empty, trailing `~~` if compare empty).
 */
export const joinPairForUrl = (
  a: string | undefined,
  b: string | undefined
): string | undefined => {
  const sa = sanitizeForDelimiter(a ?? "");
  const sb = sanitizeForDelimiter(b ?? "");
  if (!sa && !sb) return undefined;
  if (sa && sb && sa === sb) return sa;
  if (sa && !sb) return `${sa}${PAIR_DELIMITER}`;
  if (!sa && sb) return `${PAIR_DELIMITER}${sb}`;
  return `${sa}${PAIR_DELIMITER}${sb}`;
};

export type FlatCompareFormValues = Pick<
  SearchParamsType,
  "env1" | "env2" | "pipeline1" | "pipeline2" | "pit1" | "pit2" | "task1" | "task2"
>;

/**
 * Flat internal form state → compact URL param record (`env`, `pipeline`, …).
 */
export const serializeFormParams = (
  flat: Partial<FlatCompareFormValues>
): Record<string, string> => {
  const out: Record<string, string> = {};

  const addPair = (key: string, a: string | undefined, b: string | undefined) => {
    const v = joinPairForUrl(a, b);
    if (v !== undefined) out[key] = v;
  };

  addPair("env", flat.env1, flat.env2);
  addPair("pipeline", flat.pipeline1, flat.pipeline2);
  addPair("pit", flat.pit1, flat.pit2);
  addPair(
    "task",
    flat.task1?.trim() ? flat.task1 : undefined,
    flat.task2?.trim() ? flat.task2 : undefined
  );

  return out;
};

/**
 * URL search params → flat internal `{ env1, env2, … }`.
 */
export const deserializeFormParams = (
  searchParams: URLSearchParams
): Partial<SearchParamsType> => {
  const readPair = (key: string): [string, string] | undefined => {
    const raw = decodeRawParam(searchParams.get(key));
    return raw !== undefined ? splitPairParam(raw) : undefined;
  };

  const env = readPair("env");
  const pipeline = readPair("pipeline");
  const pit = readPair("pit");
  const task = readPair("task");

  return {
    ...(env && { env1: env[0], env2: env[1] }),
    ...(pipeline && { pipeline1: pipeline[0], pipeline2: pipeline[1] }),
    ...(pit && { pit1: pit[0], pit2: pit[1] }),
    ...(task && { task1: task[0], task2: task[1] }),
  };
};

/**
 * Merge compact form params into the current search string, preserving non-form keys (e.g. `tab`).
 * Query values use encodeURIComponent except `:` is kept literal for PITs.
 */
export const buildRawSearchString = (
  compactForm: Record<string, string>,
  currentSearchParams: URLSearchParams
): string => {
  const formKeys = new Set(COMPARE_RUNS_QUERY_PARAM_NAMES as readonly string[]);
  const segments: string[] = [];

  for (const [k, v] of currentSearchParams.entries()) {
    if (!formKeys.has(k)) {
      segments.push(`${encodeURIComponent(k)}=${encodeCompareRunsQueryValue(v)}`);
    }
  }

  for (const key of COMPARE_RUNS_QUERY_PARAM_NAMES) {
    const v = compactForm[key];
    if (v !== undefined && v !== "") {
      segments.push(`${encodeURIComponent(key)}=${encodeCompareRunsQueryValue(v)}`);
    }
  }

  return segments.length > 0 ? segments.join("&") : "";
};

export const getFlatFormStateValues = (formState: ComparativeFormAtomTuples) => ({
  env1: formState.env1[0],
  env2: formState.env2[0],
  pipeline1: formState.pipeline1[0],
  pipeline2: formState.pipeline2[0],
  pipelineId1: formState.pipelineId1[0],
  pipelineId2: formState.pipelineId2[0],
  pit1: formState.pit1[0],
  pit2: formState.pit2[0],
  task1: formState.task1[0],
  task2: formState.task2[0],
});

export const getGroupsFromFormState = (formState: ComparativeFormAtomTuples) => {
  const { env1, env2, pipeline1, pipeline2, pit1, pit2, task1, task2 } =
    getFlatFormStateValues(formState);
  return {
    group1: { env: env1, pipeline: pipeline1, pit: pit1, task: task1 },
    group2: { env: env2, pipeline: pipeline2, pit: pit2, task: task2 },
  };
};
